import React, { useMemo } from 'react';
import { Box, EmptyDashboardSectionIcon, Text } from '@common-fe/common-fe';

import { OrganizationTypes } from '@/common/constants';

import { useCurrentOrganization } from '../../hooks';
import EmployerCalendarDashboard from '../Statistics/EmployerCalendarDashboard';

import useGetCIPStatisticQuery from './CIPStatistic/queries/useGetCIPStatistic.query';
import useGetDelinquentClaimsStatisticQuery from './DelinquentClaimsStatistic/queries/useGetDelinquentClaimsStatistic.query';
import { useGetFraudReviewStatisticQuery } from './FraudReviewStatistic/queries';
import { useSubstantiationRateQuery } from './SubstantiationRateStatistic/queries';
import useGetUndeliverableMailStatisticQuery from './UndeliverableMailStatistic/queries/useGetUndeliverableMailStatistic.query';
import CIPStatistic from './CIPStatistic';
import DelinquentClaimsStatistic from './DelinquentClaimsStatistic';
import FraudReviewStatistic from './FraudReviewStatistic';
import {
  NarrowStatistic,
  STATISTICS_NARROW_SPAN,
  STATISTICS_ROW_UNITS,
  StatisticsGrid,
  StatisticsRow,
  WideStatistic,
} from './NewStatistics.styles';
import { StatisticsItem, StatisticsRowModel } from './NewStatistics.types';
import { useGetPlanCIPConfigQuery } from './queries';
import SubstantiationRateStatistic from './SubstantiationRateStatistic';
import UndeliverableMailStatistic from './UndeliverableMailStatistic';

/** A row pairs at most this many narrow metrics with a single wide metric. */
const MAX_NARROW_PER_ROW = 2;

const NewStatisticsContainer = () => {
  const { isLoading: isConfigLoading, data: configData } = useGetPlanCIPConfigQuery();
  const { isEmpty } = useGetCIPStatisticQuery();

  const {
    observingOrganization: { type },
  } = useCurrentOrganization();

  const isEmployerLevel = useMemo(
    () => type === OrganizationTypes.employer || type === OrganizationTypes.company,
    [type]
  );
  const isSubsidiaryLevel = useMemo(() => type === OrganizationTypes.subsidiary, [type]);

  const isCIPEmpty = useMemo(() => {
    return (isEmpty && !isEmployerLevel && !isSubsidiaryLevel) || !configData.isShowed || isConfigLoading;
  }, [configData.isShowed, isConfigLoading, isEmployerLevel, isEmpty, isSubsidiaryLevel]);

  const isReplenishmentEmpty = useMemo(() => {
    return !isEmployerLevel && !isSubsidiaryLevel;
  }, [isEmployerLevel, isSubsidiaryLevel]);
  const {
    isLoading: isSubstantiationRateLoading,
    isEmpty: isSubstantiationRateEmpty,
    data: substantiationData,
  } = useSubstantiationRateQuery();

  const { isEmpty: isUndeliverableMailEmpty } = useGetUndeliverableMailStatisticQuery();

  const { isEmpty: isDelinquentClaimsEmpty, isLoading: isDelinquentLoading } =
    useGetDelinquentClaimsStatisticQuery();
  const { isEmpty: isFraudReviewEmpty } = useGetFraudReviewStatisticQuery();

  const isSectionEmpty = useMemo(() => {
    return (
      isCIPEmpty &&
      isSubstantiationRateEmpty &&
      isUndeliverableMailEmpty &&
      isDelinquentClaimsEmpty &&
      isFraudReviewEmpty
    );
  }, [
    isCIPEmpty,
    isDelinquentClaimsEmpty,
    isFraudReviewEmpty,
    isSubstantiationRateEmpty,
    isUndeliverableMailEmpty,
  ]);

  const statisticsItems = useMemo<StatisticsItem[]>(() => {
    const items: StatisticsItem[] = [];

    if (!isSubstantiationRateEmpty) {
      items.push({
        key: 'substantiation-rate',
        content: (
          <SubstantiationRateStatistic data={substantiationData} isLoading={isSubstantiationRateLoading} />
        ),
      });
    }

    if (!isUndeliverableMailEmpty) {
      items.push({ key: 'undeliverable-mail', content: <UndeliverableMailStatistic /> });
    }

    if (!isFraudReviewEmpty) {
      items.push({ key: 'fraud-review', content: <FraudReviewStatistic /> });
    }

    if (!isConfigLoading) {
      if (!isCIPEmpty) {
        items.push({ key: 'cip-statistic', isWide: true, content: <CIPStatistic /> });
      } else if (isDelinquentClaimsEmpty) {
        items.push({
          key: 'no-metrics',
          isWide: true,
          content: (
            <Box direction="column" height={{ min: '132px' }} fill align="center" justify="center">
              <EmptyDashboardSectionIcon size="72px" color="iconDisabled" />
              <Text size="small" color="textSecondary">
                No other metrics available
              </Text>
            </Box>
          ),
        });
      }

      if (!isDelinquentClaimsEmpty) {
        items.push({
          key: 'delinquent-claims',
          isWide: true,
          content: <DelinquentClaimsStatistic isLoading={isDelinquentLoading} />,
        });
      }
    }
    return items;
  }, [
    isDelinquentLoading,
    isCIPEmpty,
    isConfigLoading,
    isDelinquentClaimsEmpty,
    isFraudReviewEmpty,
    isSubstantiationRateEmpty,
    isSubstantiationRateLoading,
    isUndeliverableMailEmpty,
    substantiationData,
  ]);

  // Pack the metrics into rows of 4 units the way the design does: each wide metric anchors a row
  // and takes up to two of the still-unplaced narrow metrics in front of it, then stretches to fill
  // the row's remaining units. So with Substantiation + Undeliverable + Fraud Review + CIP +
  // Delinquent Claims the rows are [Sub(1) Undel(1) CIP(2)] and [Fraud(1) Delinquent(3)], and a
  // wide metric with no narrow metric beside it fills the full 4 units. Narrow metrics left over
  // after every wide row (or when there is no wide metric) sit on their own row, one unit each.
  const rows = useMemo<StatisticsRowModel[]>(() => {
    const narrowItems = statisticsItems.filter((item) => !item.isWide);
    const wideItems = statisticsItems.filter((item) => item.isWide);

    const packed: StatisticsRowModel[] = [];
    let narrowCursor = 0;

    wideItems.forEach((wide) => {
      const narrowsForRow = narrowItems.slice(narrowCursor, narrowCursor + MAX_NARROW_PER_ROW);
      narrowCursor += narrowsForRow.length;

      packed.push({
        key: `row-${packed.length}`,
        cells: [
          ...narrowsForRow.map((item) => ({ ...item, span: STATISTICS_NARROW_SPAN })),
          { ...wide, span: STATISTICS_ROW_UNITS - narrowsForRow.length },
        ],
      });
    });

    // Narrow metrics that no wide row could host (or the whole set when there is no wide metric)
    // form their own row, one unit each.
    const leftoverNarrows = narrowItems.slice(narrowCursor);
    if (leftoverNarrows.length) {
      packed.push({
        key: `row-${packed.length}`,
        cells: leftoverNarrows.map((item) => ({ ...item, span: STATISTICS_NARROW_SPAN })),
      });
    }

    return packed;
  }, [statisticsItems]);

  return (
    <Box direction="column" gap="spacing24">
      {!isSectionEmpty && (
        <Box
          data-testid="dashboard_statistics"
          background="module"
          pad="spacing24"
          direction="column"
          round="moduleRound"
        >
          {/* Each row is a 4-unit CSS grid (see NewStatistics.styles): narrow metrics span one unit
           * and the wide metric stretches to fill the row's remaining units. */}
          <StatisticsGrid data-testid="dashboard_statistics_grid">
            {rows.map((row) => (
              <StatisticsRow key={row.key} data-testid="dashboard_statistics_row">
                {row.cells.map(({ key, isWide, span, content }) =>
                  isWide ? (
                    <WideStatistic
                      key={key}
                      data-testid="dashboard_statistics_wide"
                      data-span={span}
                      $span={span}
                    >
                      {content}
                    </WideStatistic>
                  ) : (
                    <NarrowStatistic
                      key={key}
                      data-testid="dashboard_statistics_narrow"
                      data-span={span}
                      $span={span}
                    >
                      {content}
                    </NarrowStatistic>
                  )
                )}
              </StatisticsRow>
            ))}
          </StatisticsGrid>
        </Box>
      )}

      {!isReplenishmentEmpty && <EmployerCalendarDashboard />}
    </Box>
  );
};

export default NewStatisticsContainer;
